import { Injectable } from '@angular/core';

/**
 * Owns every `localStorage` read/write that used to live inside
 * `AuthService`. Extracting this in Phase 3 of the Frontend Quality Plan
 * gives us:
 *
 *  1. A single place to swap `localStorage` for an encrypted-at-rest
 *     `SecureStorageService` in Phase 5 (just change the body of the
 *     private `read`/`write`/`remove` helpers).
 *  2. Easy, DI-injectable fakes in unit tests (no global `localStorage`
 *     mocking needed).
 *  3. A thin surface that will eventually back the workspace + session
 *     stores introduced in Phase 4.
 *
 * Keep this service side-effect-free outside of storage mutations; higher
 * behaviors (Firebase auth state, user profile sync) belong in the
 * upcoming `AuthenticationService` / `UserProfileService`.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly REMEMBER_ME_KEY = 'proporio_remember_me';
  private readonly REMEMBER_ME_LOGIN_AT_KEY = 'proporio_remember_me_login_at';
  private readonly REMEMBERED_EMAIL_KEY = 'proporio_remembered_email';
  private readonly PENDING_INVITATION_KEY = 'proporio_pending_invitation';
  private readonly CURRENT_WORKSPACE_KEY = 'currentWorkspaceId';

  /** Legacy plaintext-credentials blob that older builds may have written. */
  private readonly LEGACY_AUTH_KEY = 'proporio_auth';

  /** Legacy service-provider invitation token from an earlier flow. */
  private readonly LEGACY_PROVIDER_INVITATION_KEY = 'pendingProviderInvitationToken';

  setRememberMe(remember: boolean): void {
    this.write(this.REMEMBER_ME_KEY, remember ? 'true' : 'false');
  }

  getRememberMe(): boolean {
    return this.read(this.REMEMBER_ME_KEY) === 'true';
  }

  /**
   * Timestamp (ms since epoch) of the last "Remember me" login. Used to
   * enforce a soft 30-day expiry — past that, the user must re-authenticate
   * even if Firebase still has a valid refresh token. Absent (or null) means
   * the user is not in remember-me mode (session-only persistence).
   */
  setRememberMeLoginAt(timestampMs: number): void {
    this.write(this.REMEMBER_ME_LOGIN_AT_KEY, String(timestampMs));
  }

  getRememberMeLoginAt(): number | null {
    const v = this.read(this.REMEMBER_ME_LOGIN_AT_KEY);
    if (v === null) return null;
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  }

  clearRememberMeLoginAt(): void {
    this.remove(this.REMEMBER_ME_LOGIN_AT_KEY);
  }

  storeRememberedEmail(email: string): void {
    this.write(this.REMEMBERED_EMAIL_KEY, email);
    // Best-effort cleanup of the old, unsafe credential blob.
    this.scrubLegacyAuth();
  }

  getRememberedEmail(): string | null {
    return this.read(this.REMEMBERED_EMAIL_KEY);
  }

  clearRememberedEmail(): void {
    this.remove(this.REMEMBERED_EMAIL_KEY);
    this.scrubLegacyAuth();
  }

  storePendingInvitation(invitationId: string): void {
    this.write(this.PENDING_INVITATION_KEY, invitationId);
  }

  /**
   * Read the pending invitation id WITHOUT clearing it. Use when a screen
   * only needs to know an invite exists (banner, routing decision) so a
   * later consumer can still claim it (#584 / #588).
   */
  peekPendingInvitation(): string | null {
    return this.read(this.PENDING_INVITATION_KEY);
  }

  /**
   * Returns the pending invitation id and clears it. Designed as a
   * one-shot consumer because the downstream flow treats reading as
   * "invitation claimed".
   */
  consumePendingInvitation(): string | null {
    const invitationId = this.read(this.PENDING_INVITATION_KEY);
    if (invitationId) {
      this.remove(this.PENDING_INVITATION_KEY);
    }
    return invitationId;
  }

  hasPendingInvitation(): boolean {
    return this.read(this.PENDING_INVITATION_KEY) !== null;
  }

  scrubLegacyAuth(): void {
    this.remove(this.LEGACY_AUTH_KEY);
  }

  clearLegacyProviderInvitation(): void {
    this.remove(this.LEGACY_PROVIDER_INVITATION_KEY);
  }

  /** Persisted workspace selection — survives reload, scoped per browser. */
  storeCurrentWorkspaceId(workspaceId: string): void {
    this.write(this.CURRENT_WORKSPACE_KEY, workspaceId);
  }

  getCurrentWorkspaceId(): string | null {
    return this.read(this.CURRENT_WORKSPACE_KEY);
  }

  clearCurrentWorkspaceId(): void {
    this.remove(this.CURRENT_WORKSPACE_KEY);
  }

  /**
   * Signup-flow side-keys: provider-invitation token + account-type hint.
   * Both are read once after signup completes and then cleared by the
   * post-signup flow. Kept here so signup components don't touch
   * `localStorage` directly.
   */
  private readonly PENDING_PROVIDER_INVITATION_KEY = 'pendingProviderInvitationToken';
  private readonly SIGNUP_ACCOUNT_TYPE_KEY = 'signupAccountType';

  storePendingProviderInvitation(token: string): void {
    this.write(this.PENDING_PROVIDER_INVITATION_KEY, token);
  }

  getPendingProviderInvitation(): string | null {
    return this.read(this.PENDING_PROVIDER_INVITATION_KEY);
  }

  clearPendingProviderInvitation(): void {
    this.remove(this.PENDING_PROVIDER_INVITATION_KEY);
  }

  storeSignupAccountType(accountType: string): void {
    this.write(this.SIGNUP_ACCOUNT_TYPE_KEY, accountType);
  }

  getSignupAccountType(): string | null {
    return this.read(this.SIGNUP_ACCOUNT_TYPE_KEY);
  }

  clearSignupAccountType(): void {
    this.remove(this.SIGNUP_ACCOUNT_TYPE_KEY);
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      // localStorage may be unavailable (SSR, Safari private mode, etc.)
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* swallow */
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* swallow */
    }
  }
}
